import Vue from 'vue';
import i18n from '@/plugins/i18n';

const setText = (text) => {
  return text?.[i18n.locale] || text?.['nl'] || text || '' // nl fallback
}

// Brussels Hoofdstedelijk Gewest municipality codes are 21001-21019 //
const isBrusselsCode = (code) => +code >= 21001 && +code <= 21019

const groupMunicipalityOptions = (options) => {
  let allowed = [], notAllowed = []
  options
    .sort((a, b) => setText(a.text).localeCompare(setText(b.text)))
    .forEach(opt => opt.locationSelectionAllowed ? allowed.push(opt) : notAllowed.push(opt))

  return [
    ...( notAllowed.length
      ? [
          { text: `⬇️ ${i18n.t('processes.fields.selectLocationOnly')}`, value: null, disabled: true },
          ...notAllowed
        ]
      : []
    ),
    ...( allowed.length
      ? [
          { text: `⬇️ ${i18n.t('processes.fields.createAndSelectLocation')}`, value: null, disabled: true },
          ...allowed
        ]
      : []
    )
  ]
}

const state = {
  tenant: process.env.VUE_APP_TENANT,
  tenantName: '',
  tenantAcronym: '',
  processType: {},
  image: {},
  featureAccess: {},
  mapOptions: {},
  visibleRecordsByStatus: {},
  usesPlannedRealisationFields: true,
  useAdditionalCoordinates: false,
  cpos: {},
  loading: false,
  tags: [],
  toasts: [],
  contractors: [],
  pids: [],
  regionalProjectLeaders: [],
  workflows: {},
  stakeholders: [],
  gridoperators: [],
  workflowspecs: {},
  selectOptions: {},
  selectOptionLabelsByValue: {},
  municipalityOptions: [],
  municipalityOptionsGrouped: [],
  allMunicipalityOptions: [],
  chargingStationVersions: [],
  cancelReasons: [],
  onHoldReasons: [],
  reasonOptions: [],
  monitoring: {},
  defaultAssetTableFields: [],
  defaultRequestTableFields: [],
  defaultProcessTableFields: [],
  processTypes: [],
  chargingpointStatuses: [],
  processesConfig: {},
  createAssetForm: [],
  baseUrl: '',
  workflowUsers: [],
  processStepLabels: {},
  processFieldLabels: {}
};

const getters = {
  getTenant: state => {
    return { slug: state.tenant, name: state.tenantName, acronym: state.tenantAcronym };
  },
  isCurrentTenant:
    state =>
    ({ tenant }) =>
      state.tenant === tenant,
  getTags: state => state.tags,
  getEnabledTags: state => state.tags.filter(tag => !!tag.enabled && !tag.deleted_at),
  getExistingTags: state => state.tags.filter(tag => !tag.deleted_at),
  getLabelsByTagUuid: state =>
    state.tags.reduce((result, tag) => {
      result[tag.uuid] = tag.label;
      return result;
    }, {}),

  getRecordTagLabels:
    state =>
    ({ record }) => {
      return record.tags.map(uuid => state.tags.find(tag => uuid === tag.uuid)?.label);
    },

  getPIDs: state => state.pids,
  getRegionalProjectLeaders: state => state.regionalProjectLeaders,
  getBaseUrl: state => state.baseUrl,
  getEnabledPids: state => state.pids.filter(pid => !!pid.enabled),
  getLabelsByPidUuid: state =>
    state.pids.reduce((result, pid) => {
      result[pid.uuid] = pid.label;
      return result;
    }, {}),
  getToasts: state => state.toasts,
  getContractors: state => state.contractors,
  getSelectableWorkflowUsers: (state, getters, { labelType = 'text' }) => {
    return getters.getWorkflowUsers
      .filter(user => !user.blocked)
      .map(user => ({ value: user.user_id, [labelType]: user.name }))
  },
  getWorkflowUsers: state => {
    return state.workflowUsers
  },
  // getWorkflowUsers: state => state.workflowUsers.map(user => ({ value: user.user_id, text: user.name, email: user.email, blocked: user.blocked })),
  getExistingContractors: state => state.contractors,
  getEnabledContractors: state => state.contractors,
  getLabelsByContractorUuid: state =>
    state.contractors.reduce((result, contractor) => {
      result[contractor.uuid] = contractor.label;
      return result;
    }, {}),

  getContractorByUuid:
    state =>
    ({ uuid }) =>
      state.contractors.find(contractor => contractor.uuid === uuid),

  getWorkflowUuidByName:
    state =>
    ({ name }) =>
      state.workflows[name] || null,
  getWorkflowSpecByUuid:
    state =>
    ({ uuid }) =>
      state.workflowspecs[uuid] || null,
  getWorkflowSpecByName:
    state =>
    ({ name }) => {
      let uuid = state.workflows[name] || null;
      return uuid ? state.workflowspecs[uuid] || null : null;
    },

  getSelectOptionsByName:
    state =>
    ({ name }) => {
      return state.selectOptions[name] || [];
    },
  getSelectOptionLabelByValue:
    state =>
    ({ name, value }) => {
      return (state.selectOptionLabelsByValue[name] || {})[value] || null;
    },

  getCategoryOptions: (state, getters) => {
    const categories = getters.getSelectOptionsByName({ name: 'processCategories' })
    return categories.map(category => {
      category.text = i18n.t(`processes.sidebar.process_categories.${category.value}`)
      return category
    })
  },
  getCategoryOptionsByContext:
    (state, getters) =>
    ({ context }) => {
      let options = getters.getSelectOptionsByName({ name: 'processCategories' });
      return context === null ? options : options.filter(option => option?.meta?.context === context);
    },
  getVkbStatuses: () => state.selectOptions?.vkb?.map(status => status.value) || [],
  getMunicipalityOptions: state => {
    const municipalities = JSON.parse(JSON.stringify(state.municipalityOptions))
    return municipalities.map(o => { o.text = setText(o.text); return o })
  },
  // Tenant-independent list of all municipalities, e.g. for private/manual asset flows //
  // that may reference a municipality outside this tenant's own configured list //
  getAllMunicipalityOptions: state => {
    const municipalities = JSON.parse(JSON.stringify(state.allMunicipalityOptions))
    return municipalities.map(o => { o.text = setText(o.text); return o })
  },
  // Same as getAllMunicipalityOptions, but overlays this tenant's own contractor //
  // default (from getMunicipalityOptions) where one is configured for that municipality //
  getAllMunicipalityOptionsWithContractor: (state, getters) => {
    const contractorByCode = new Map(
      getters.getMunicipalityOptions.filter(o => o.contractor).map(o => [o.value, o.contractor])
    )
    return getters.getAllMunicipalityOptions.map(o =>
      contractorByCode.has(o.value) ? { ...o, contractor: contractorByCode.get(o.value) } : o
    )
  },
  getMunicipalityOptionsByProcessType: (state, getters) => ({ type }) => {
    const options = {
      brussels: [],
      wallonia: []
    }
    getters.getMunicipalityOptions
      .forEach(o => {
        isBrusselsCode(o.value) ? options.brussels.push(o) : options.wallonia.push(o)
      })

    return options[type]
  },
  getMunicipalityOptionsGrouped: (state, getters, rootState, rootGetters) => {
    const type = rootGetters['processes/getCurrentProcessType']
    if (!type) return []

    const hasMunicipalitiesByType = ['brussels', 'wallonia'].includes(type)
    const options = hasMunicipalitiesByType
      ? getters.getMunicipalityOptionsByProcessType({ type })
      : getters.getMunicipalityOptions

    return groupMunicipalityOptions(options) || []
  },
  getCharchingStationVersions: state => state.chargingStationVersions,
  getOnHoldReasons: state => {
    const onholdReasons = JSON.parse(JSON.stringify(state.onHoldReasons))
    return onholdReasons.map(reason => {
      reason.text = reason.text[i18n.locale] || reason.text
      return reason
    })
  },
  getCancelReasons: state => {
    const cancelReasons = JSON.parse(JSON.stringify(state.cancelReasons))
    return cancelReasons.map(reason => {
      reason.text = reason.text[i18n.locale] || reason.text
      return reason
    })
  },
  getReasonOptions: state => {
    const reasonOptions = JSON.parse(JSON.stringify(state.reasonOptions))
    return reasonOptions.map(reason => {
      reason.text = reason.text[i18n.locale] || reason.text
      return reason
    })
  },
  // eslint-disable-next-line no-unused-vars
  getSteps: (state, getters, rootState, rootGetters) => {
    const type = rootGetters['processes/getCurrentProcessType']
    //console.log('tenant getSteps', type, state.processesConfig[type])
    return type ? state.processesConfig[type]?.steps : []
  },
  getBranchSource: (state, getters, rootState, rootGetters) => {
    const type = rootGetters['processes/getCurrentProcessType']
    return type ? state.processesConfig[type]?.branchSource || null : null
  },
  getTenantStakeholders: state => ({ category }) => category ? state.stakeholders.filter(stakeholder => stakeholder.category === category) : state.stakeholders,
  getProcessesConfig: state => state.processesConfig,
  getProcessStepLabels: state => state.processStepLabels,
  getProcessFieldLabels: state => state.processFieldLabels,
  //getSteps: state => state.steps,
  getImage: state => state.image,
  getMapOptions: state => state.mapOptions,
  getUsesPlannedRealisationFields: state => state.usesPlannedRealisationFields,
  getUseAdditionalCoordinates: state => state.useAdditionalCoordinates,
  getCpos: state => state.cpos,
  getGridOperators: state => state.gridoperators.sort((a, b) => a.text.localeCompare(b.text)),
  getMonitoringConfig: state => state.monitoring,
  getFeatureAccess: state => state.featureAccess,
  getVisibleRecordsByStatus: state => state.visibleRecordsByStatus,
  getDefaultAssetTableFields: state => state.defaultAssetTableFields.map(column => {
    column.label = i18n.t(`recordList.${column.field}`)
    return column
  }),
  getDefaultRequestTableFields: state => state.defaultRequestTableFields,
  getDefaultProcessTableFields: state => state.defaultProcessTableFields,
  getProcessTypes: state => state.processTypes,
  getActiveProcessTypes: (state) => state.processTypes.filter(type => !type.hidden),
  getChargingpointStatuses: state => state.chargingpointStatuses,
  getAssetForm: state => state.createAssetForm
};
const actions = {
  setTags ({ commit }, { tags }) {
    commit('setTags', { tags })
  },
  setConfig ({ commit }, { config } ) {
    console.log('store action setConfig', config)
    commit('setConfig', { config })
  },
  setToast(_, { message, error, variant, delay = 1000 }) {
    setTimeout(() => {
        this.$bvToast.toast(message, {
        title: variant ? 'Waarschuwing' : error ? 'Fout!' : 'Succes!',
        variant: variant || (error ? 'danger' : 'success'),
        toaster: 'b-toaster-top-right',
        solid: true,
        delay: delay * 2
      });
    }, delay);
  },

  loadConfig: async ({ state, commit }, { token }) => {
    if (state.loading) return;

    commit('setLoadingState', { loading: true });

    try {
      const response = await fetch('/api/config', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load config');
      }

      const resp = await response.json();

      commit('setConfig', { config: resp.data });
    } finally {
      commit('setLoadingState', { loading: false });
    }
  },

  loadWorkflowSpecs: async ({ state, commit }, { token }) => {
    let uuids = Object.values(state.workflows);

    for (let i = 0; i < uuids.length; i++) {
      const api = await fetch('/api/workflowspecification', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          uuid: uuids[i]
        })
      });

      if (!api.ok) {
        throw new Error('Failed to load config');
      }

      const response = await api.json();

      commit('setWorkflowSpecByUuid', {
        uuid: uuids[i],
        spec: response.data
      });
    }
  }
};
const mutations = {
  setTags(state, { tags }) {
    state.tags = tags;
  },

  setLoadingState(state, { loading }) {
    state.loading = loading;
  },

  setConfig(state, { config }) {
    if (config.tenant !== state.tenant) {
      throw new Error('Wrong tenant config');
    }

    state.tenantAcronym = config.tenantAcronym;
    state.tenantName = config.tenantName;
    state.image = config.tenantImage || config.image;
    state.featureAccess = config.featureAccess || {};
    state.mapOptions = config.mapOptions;
    state.visibleRecordsByStatus = config.visibleRecordsByStatus || {};
    state.usesPlannedRealisationFields = config.usesPlannedRealisationFields;
    state.useAdditionalCoordinates = config.useAdditionalCoordinates;
    state.stakeholders = config.stakeholders;
    state.gridoperators = config.gridoperators?.map(c => ({ text: c.label, value: c.value })) || [];
    state.cpos = config.cpos?.map(c => ({ text: c.name, value: c.uuid })) || [];
    state.tags = config.tags || [];
    state.pids = (config.pids || []).sort((a, b) => a.label.localeCompare(b.label));
    state.regionalProjectLeaders = config.regionalProjectLeaders
    state.toasts = config.toasts || [];
    // config.contractors already arrives shaped as {uuid, value, label, enabled} from
    // functions/config/config.js — no client-side remap needed (or safe: that endpoint
    // has no .name field on these objects, only .label).
    state.contractors = (config.contractors || []).sort((a, b) => a.label.localeCompare(b.label));
    state.workflows = config.workflows || {};
    state.selectOptions = config.selectOptions || {};
    state.baseUrl = config.baseUrl;
    state.municipalityOptions = config.municipalityOptions || [];
    state.allMunicipalityOptions = config.allMunicipalityOptions || [];
    state.chargingStationVersions = config.chargingStationVersions || [];
    state.cancelReasons = config.cancelReasons || [];
    state.onHoldReasons = config.onHoldReasons || [];
    state.reasonOptions = config.reasonOptions || [];
    state.processesConfig = config.processesConfig || {};
    state.createAssetForm = config.createAssetForm || []
    state.workflowUsers = config.workflowUsers || []

    state.monitoring = config.monitoring || {};
    state.defaultAssetTableFields = config.defaultAssetTableFields || [];
    state.defaultRequestTableFields = config.defaultRequestTableFields || [];
    state.defaultProcessTableFields = config.defaultProcessTableFields || [];
    state.processTypes = config.processTypes || []
    state.chargingpointStatuses = config.chargingpointStatuses || []

    state.selectOptionLabelsByValue = Object.keys(state.selectOptions).reduce((result, key) => {
      result[key] = state.selectOptions[key].reduce((optionLabelsByValue, option) => {
        optionLabelsByValue[option.value] = option.text || '';
        return optionLabelsByValue;
      }, {});
      return result;
    }, {});

    Object.keys(config.processesConfig).forEach(processType => {
      state.processStepLabels[processType] =
        config.processesConfig[processType].steps.map(step => ({ label: step.label }))
    });
    // Create story point for field translations => getProcessFieldLabels //
    ['private', 'brussels', 'wallonia', 'takeover'].forEach(processType => {
      config.processesConfig[processType]?.steps.forEach(step => {
        if (step.form) {
          step.form.forEach(component => {
            if (component?.fields) {
              Object.keys(component.fields).forEach(field => state.processFieldLabels[field] = component.fields[field].title )
            }
          })
        }
      })
    })
  },

  setWorkflowSpecByUuid(state, { uuid, spec }) {
    Vue.set(state.workflowspecs, uuid, spec);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
